import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
    UserPlus,
    Target,
    Trophy,
    Percent,
    Building2,
    Contact,
    ListTodo,
    AlertTriangle,
    TrendingUp,
} from "lucide-react";

const money = (v) =>
    `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Stat({ icon: Icon, label, value, hint, to, testid }) {
    const content = (
        <div className="sc-card p-4 h-full" data-testid={testid}>
            <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs uppercase tracking-wider">{label}</span>
                {Icon && <Icon className="w-4 h-4" strokeWidth={1.75} />}
            </div>
            <div className="mt-2 text-2xl font-semibold tracking-tight font-mono tabular-nums">{value ?? "—"}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </div>
    );

    return to ? <Link to={to}>{content}</Link> : content;
}

export default function CrmDashboard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/crm/dashboard");
                setData(data && typeof data === "object" ? data : {});
            } catch (e) {
                setError(formatApiError(e));
                toast.error(formatApiError(e));
            }
        })();
    }, []);

    if (error) {
        return (
            <div className="p-3 md:p-6" data-testid="crm-dashboard-page">
                <div className="sc-card p-6 text-center text-sm text-muted-foreground">
                    Couldn't load the CRM dashboard. {error}
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-3 md:p-6" data-testid="crm-dashboard-page">
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
            </div>
        );
    }

    const leads = data.leads || {};
    const deals = data.deals || {};
    const activities = data.activities || {};

    return (
        <div className="p-3 md:p-6 space-y-5 md:space-y-6" data-testid="crm-dashboard-page">
            <div>
                <div className="sc-section-title">CRM</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Overview</h1>
            </div>

            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Leads</div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat icon={UserPlus} label="Total Leads" value={leads.total ?? "—"} to="/crm/leads" testid="crm-kpi-leads-total" />
                    <Stat icon={Target} label="Open Leads" value={leads.open ?? "—"} to="/crm/leads" testid="crm-kpi-leads-open" />
                    <Stat icon={Trophy} label="Converted" value={leads.converted ?? "—"} to="/crm/leads" testid="crm-kpi-leads-converted" />
                    <Stat
                        icon={Percent}
                        label="Conversion Rate"
                        value={leads.conversion_rate != null ? `${Number(leads.conversion_rate).toFixed(1)}%` : "—"}
                        testid="crm-kpi-leads-rate"
                    />
                </div>
            </div>

            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Deals / Pipeline</div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat icon={TrendingUp} label="Open Deals" value={deals.open_count ?? "—"} to="/crm/pipeline" testid="crm-kpi-deals-open" />
                    <Stat icon={Target} label="Open Amount" value={deals.open_amount != null ? money(deals.open_amount) : "—"} to="/crm/pipeline" testid="crm-kpi-deals-amount" />
                    <Stat icon={Trophy} label="Weighted Forecast" value={deals.weighted_forecast != null ? money(deals.weighted_forecast) : "—"} to="/crm/pipeline" testid="crm-kpi-deals-forecast" />
                    <Stat icon={Percent} label="Win Rate" value={deals.win_rate != null ? `${Number(deals.win_rate).toFixed(1)}%` : "—"} testid="crm-kpi-deals-winrate" />
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat icon={Building2} label="Accounts" value={data.accounts ?? "—"} to="/crm/accounts" testid="crm-kpi-accounts" />
                <Stat icon={Contact} label="Contacts" value={data.contacts ?? "—"} to="/crm/contacts" testid="crm-kpi-contacts" />
                <Stat icon={ListTodo} label="My Open Activities" value={activities.open ?? "—"} to="/crm/activities" testid="crm-kpi-activities-open" />
                <Stat icon={AlertTriangle} label="Overdue" value={activities.overdue ?? "—"} to="/crm/activities" testid="crm-kpi-activities-overdue" />
            </div>
        </div>
    );
}
